import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTenantStore } from '@/store/tenant.store';
import type {
  ConversationWithPreview,
  PaginatedConversations,
  PaginatedMessages,
  CreateConversationInput,
  UpdateConversationInput,
  CreateMessageInput,
  UnreadSummary,
  ConversationParticipant,
} from '@/types/messaging';
import { api } from '@/services/api';

// ============================================================================
// Conversations
// ============================================================================

export function useConversations(filters?: {
  type?: string;
  is_archived?: boolean;
  search?: string;
  incident_id?: string;
}) {
  const { activeTenant } = useTenantStore();

  return useQuery({
    queryKey: ['conversations', activeTenant?.id, filters],
    queryFn: async (): Promise<PaginatedConversations> => {
      const params = new URLSearchParams();
      if (filters?.type) params.append('type', filters.type);
      if (filters?.is_archived !== undefined) params.append('is_archived', String(filters.is_archived));
      if (filters?.search) params.append('search', filters.search);
      if (filters?.incident_id) params.append('incident_id', filters.incident_id);

      return api.get<PaginatedConversations>(`/messaging/conversations?${params.toString()}`);
    },
    enabled: !!activeTenant?.id,
    staleTime: 30000,
  });
}

export function useConversation(conversationId: string | null) {
  const { activeTenant } = useTenantStore();

  return useQuery({
    queryKey: ['conversation', conversationId],
    queryFn: async (): Promise<ConversationWithPreview> => {
      return api.get<ConversationWithPreview>(`/messaging/conversations/${conversationId}`);
    },
    enabled: !!conversationId && !!activeTenant?.id,
  });
}

export function useCreateConversation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateConversationInput): Promise<ConversationWithPreview> => {
      return api.post<ConversationWithPreview>('/messaging/conversations', input);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
}

export function useUpdateConversation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      conversationId,
      input,
    }: {
      conversationId: string;
      input: UpdateConversationInput;
    }): Promise<ConversationWithPreview> => {
      return api.patch<ConversationWithPreview>(`/messaging/conversations/${conversationId}`, input);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      queryClient.invalidateQueries({ queryKey: ['conversation', variables.conversationId] });
    },
  });
}

export function useDeleteConversation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (conversationId: string): Promise<void> => {
      await api.delete(`/messaging/conversations/${conversationId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
}

// ============================================================================
// Participants
// ============================================================================

export function useAddParticipant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      conversationId,
      userId,
      role,
    }: {
      conversationId: string;
      userId: string;
      role?: string;
    }): Promise<ConversationParticipant> => {
      return api.post<ConversationParticipant>(`/messaging/conversations/${conversationId}/participants`, {
        user_id: userId,
        role,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['conversation', variables.conversationId] });
    },
  });
}

export function useRemoveParticipant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      conversationId,
      userId,
    }: {
      conversationId: string;
      userId: string;
    }): Promise<void> => {
      await api.delete(`/messaging/conversations/${conversationId}/participants/${userId}`);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['conversation', variables.conversationId] });
    },
  });
}

// ============================================================================
// Messages
// ============================================================================

export function useMessages(
  conversationId: string | null,
  filters?: {
    before?: string;
    after?: string;
    search?: string;
    limit?: number;
  }
) {
  const { activeTenant } = useTenantStore();

  return useQuery({
    queryKey: ['messages', conversationId, filters],
    queryFn: async (): Promise<PaginatedMessages> => {
      const params = new URLSearchParams();
      if (filters?.before) params.append('before', filters.before);
      if (filters?.after) params.append('after', filters.after);
      if (filters?.search) params.append('search', filters.search);
      if (filters?.limit) params.append('limit', String(filters.limit));

      return api.get<PaginatedMessages>(`/messaging/conversations/${conversationId}/messages?${params.toString()}`);
    },
    enabled: !!conversationId && !!activeTenant?.id,
    staleTime: 10000,
  });
}

export function useCreateMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      conversationId,
      input,
    }: {
      conversationId: string;
      input: CreateMessageInput;
    }) => {
      return api.post(`/messaging/conversations/${conversationId}/messages`, input);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['messages', variables.conversationId] });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
}

export function useUpdateMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      messageId,
      content,
    }: {
      messageId: string;
      content: string;
      conversationId: string;
    }) => {
      return api.patch(`/messaging/messages/${messageId}`, { content });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['messages', variables.conversationId] });
    },
  });
}

export function useDeleteMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      messageId,
    }: {
      messageId: string;
      conversationId: string;
    }): Promise<void> => {
      await api.delete(`/messaging/messages/${messageId}`);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['messages', variables.conversationId] });
    },
  });
}

// ============================================================================
// Read Receipts
// ============================================================================

export function useMarkAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      messageId,
    }: {
      messageId: string;
      conversationId: string;
    }) => {
      return api.post(`/messaging/messages/${messageId}/read`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      queryClient.invalidateQueries({ queryKey: ['unread-summary'] });
    },
  });
}

// ============================================================================
// Reactions
// ============================================================================

export function useToggleReaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      messageId,
      emoji,
    }: {
      messageId: string;
      emoji: string;
      conversationId: string;
    }) => {
      return api.post(`/messaging/messages/${messageId}/reactions`, { emoji });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['messages', variables.conversationId] });
    },
  });
}

// ============================================================================
// Unread Summary
// ============================================================================

export function useUnreadSummary() {
  const { activeTenant } = useTenantStore();

  return useQuery({
    queryKey: ['unread-summary', activeTenant?.id],
    queryFn: async (): Promise<UnreadSummary> => {
      return api.get<UnreadSummary>('/messaging/unread');
    },
    enabled: !!activeTenant?.id,
    staleTime: 30000,
    refetchInterval: 60000, // Refetch every minute
  });
}

// ============================================================================
// Incident Thread
// ============================================================================

export function useIncidentThread(incidentId: string | null) {
  const { activeTenant } = useTenantStore();

  return useQuery({
    queryKey: ['incident-thread', incidentId],
    queryFn: async (): Promise<ConversationWithPreview> => {
      return api.get<ConversationWithPreview>(`/messaging/incidents/${incidentId}/thread`);
    },
    enabled: !!incidentId && !!activeTenant?.id,
  });
}
